/**
 * Module dependencies
 */
import React from 'react';
import { shape, bool, arrayOf, string } from 'prop-types';

import TrendEntry from '@seo-frontend-components/trends-entry';

import { useUser } from '../../components/context/user';
import { useStaticProps } from '../../components/context/static-props';
import { withComponentHandler } from '../../hocs/with-component-handler';

/**
 * @typedef {Object} Image
 * @property {string} description - The description of the image.
 * @property {string} url - The URL of the image.
 */

/**
 * @typedef {Object} Trend
 * @property {string} description - The description of the trend.
 * @property {Image[]} images - The images for the trend.
 * @property {string} keyword - The keyword for the trend.
 * @property {string} url - The URL for the trend.
 */

/**
 * @typedef {Object} TrendsCategories
 * @property {string} title - The title of the trends categories.
 * @property {Trend[]} trends - The list of trends.
 */

/**
 * CategoryTrendContainer Component
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.hasCarousel - Whether the container has a carousel.
 * @param {TrendsCategories} props.trends_categories - The trends categories data.
 * @returns {JSX.Element} The rendered component.
 */
const CategoryTrendContainer = ({ trends_categories, hasCarousel }) => {
  const namespace = 'ui-category-trends-container';
  const { deviceType } = useStaticProps();
  const { loggedIn } = useUser();

  const viewTypeClass = 'container';
  const carouselClass = hasCarousel ? 'carousel' : '';

  return (
    <section className={`${namespace} ${deviceType}`}>
      <div className={`${namespace}-${deviceType}`}>
        <h2>{trends_categories.title}</h2>
        <div className={`${namespace}-searches ${viewTypeClass} ${carouselClass}`}>
          {trends_categories.trends.map((trendEntry) => (
            <div key={`category-entry-container-${viewTypeClass}-${trendEntry.keyword}`} className="item">
              <TrendEntry
                trend={trendEntry}
                deviceType={deviceType}
                showImage={loggedIn}
                insideCarousel={false}
                preload
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

CategoryTrendContainer.propTypes = {
  hasCarousel: bool,
  trends_categories: shape({
    title: string,
    trends: arrayOf(
      shape({
        description: string.isRequired,
        images: arrayOf(
          shape({
            description: string.isRequired,
            url: string.isRequired,
          }),
        ).isRequired,
        keyword: string.isRequired,
        url: string.isRequired,
      }),
    ),
  }),
};

/**
 * Inject i18n context as props into CategoryTrendContainer.
 */

export default withComponentHandler(CategoryTrendContainer, { componentName: 'CategoryTrendContainer' });
